import axios from 'axios';

// REFONTE 2026-04-24 : URL API toujours calculee au runtime depuis
// window.location.origin. Universel PROD (erp.o29.ma) / TEST (194.163.165.246:8081)
// / localhost. Evite les soucis env corrompue (MSYS) et les builds env-specifiques.
// Note : pour dev local avec npm start sur :3000, pointer vers backend :5000 via
// la cle "proxy" de package.json ou lancer le backend sur meme origin.
export const API_URL = (typeof window !== 'undefined')
  ? window.location.origin + '/api'
  : 'http://localhost:5000/api';

// Intercepteur GLOBAL fetch — token JWT + réécriture URL pour déploiement
// En production, redirige automatiquement localhost:5000 vers l'API réelle
const _originalFetch = window.fetch;
const _LOCALHOST_API = 'http://localhost:5000/api';
const _LOCALHOST_BASE = 'http://localhost:5000';
window.fetch = function(url, options = {}) {
  let finalUrl = url;
  // Réécrire les URLs hardcodées vers l'API configurée
  if (typeof url === 'string') {
    if (url.startsWith(_LOCALHOST_API)) {
      finalUrl = API_URL + url.substring(_LOCALHOST_API.length);
    } else if (url.startsWith(_LOCALHOST_BASE + '/api')) {
      finalUrl = API_URL + url.substring(_LOCALHOST_API.length);
    }
    // Ajouter le token JWT
    const token = localStorage.getItem('token');
    if (token && (finalUrl.includes('/api/') || finalUrl.includes('localhost'))) {
      options.headers = { ...options.headers, 'Authorization': `Bearer ${token}` };
    }
  }
  return _originalFetch.call(this, finalUrl, options);
};

// Télécharge/ouvre un fichier depuis un endpoint API protégé par JWT.
// window.open()/href direct ne passent PAS par l'intercepteur fetch ci-dessus
// -> aucun header Authorization -> le garde global (app.py before_request) renvoie 401.
// Ici on fetch (token injecté par l'intercepteur) puis on sert le blob.
//
// Mode onglet (download:false) : la fenêtre est ouverte AVANT le premier await,
// donc encore dans le geste utilisateur — les bloqueurs de popup ne bloquent que
// les window.open faits après un fetch async. Si le navigateur la refuse quand
// même, fallback automatique en téléchargement (la fiabilité prime).
// IMPORTANT : appeler cette fonction directement depuis le handler de clic.
export const openProtectedFile = async (url, { download = true, filename } = {}) => {
  let tab = null;
  if (!download) {
    tab = window.open('', '_blank');   // synchrone, dans le geste utilisateur
    if (tab) {
      try { tab.document.write('Chargement du document…'); } catch (_) {}
    }
  }
  const triggerDownload = (blobUrl) => {
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = filename || 'document';
    document.body.appendChild(a);
    a.click();
    a.remove();
  };
  try {
    const res = await fetch(url);
    if (!res.ok) {
      let msg = `Erreur ${res.status}`;
      try { const d = await res.json(); msg = d.error || msg; } catch (_) {}
      throw new Error(msg);
    }
    const blob = await res.blob();
    const blobUrl = URL.createObjectURL(blob);
    if (tab) {
      tab.location = blobUrl;          // le PDF s'affiche, imprimable direct
    } else {
      triggerDownload(blobUrl);        // mode download, ou popup bloquée
    }
    setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
  } catch (e) {
    if (tab) { try { tab.close(); } catch (_) {} }
    throw e;
  }
};

// Helper pour les URLs d'images/uploads — utilisable partout
export const getServerUrl = (path) => {
  if (!path) return '';
  if (path.startsWith('http')) return path;
  // En dev: localhost:5000, en prod: API_URL sans /api
  const baseUrl = API_URL.replace(/\/api$/, '');
  return `${baseUrl}${path}`;
};

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 10000,
});


api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

api.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error.response?.status;
    const data = error.response?.data;

    if (status === 401) {
      const isLoginRequest = error.config?.url?.includes('/auth/login');
      if (!isLoginRequest && window.location.pathname !== '/login') {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        localStorage.removeItem('workspaceContext');
        sessionStorage.removeItem('session_active');
        window.location.href = '/login';
      }
    } else if (status >= 500) {
      // Erreur serveur — afficher le code d'erreur si disponible
      window.dispatchEvent(new CustomEvent('erp:error', {
        detail: {
          message: data?.error || 'Erreur serveur inattendue',
          code: data?.code || null,
          status,
        }
      }));
    } else if (status >= 400 && status !== 401 && status !== 404 && status !== 409) {
      // Erreur métier (400, 403, 422…) — afficher le message
      // 409 Conflict = business logic, toujours géré localement par le composant (pas de toast global)
      const msg = data?.error || data?.message || `Erreur ${status}`;
      window.dispatchEvent(new CustomEvent('erp:error', {
        detail: { message: msg, code: data?.code || null, status }
      }));
    }

    return Promise.reject(error);
  }
);

export const authAPI = {
  login: (credentials) => api.post('/auth/login', credentials),
  me: () => api.get('/auth/me'),
  changePassword: (data) => api.post('/auth/change-password', data),
};

export const usersAPI = {
  getAll: () => api.get('/users'),
  getOne: (id) => api.get(`/users/${id}`),
  create: (data) => api.post('/users', data),
  update: (id, data) => api.put(`/users/${id}`, data),
  delete: (id) => api.delete(`/users/${id}`),
};

export const boutiquesAPI = {
  getAll: () => api.get('/boutiques'),
  getOne: (id) => api.get(`/boutiques/${id}`),
  create: (data) => api.post('/boutiques', data),
  update: (id, data) => api.put(`/boutiques/${id}`, data),
  delete: (id) => api.delete(`/boutiques/${id}`),
};

export const bijouxAPI = {
  getAll: (params) => api.get('/bijoux', { params }),
  getById: (id) => api.get(`/bijoux/${id}`),
  create: (data) => api.post('/bijoux', data),
  update: (id, data) => api.put(`/bijoux/${id}`, data),
  delete: (id) => api.delete(`/bijoux/${id}`),
  getLocalisation: (id) => api.get(`/bijoux/${id}/localisation`),
  getHistorique: (id) => api.get(`/bijoux/${id}/historique`),
  getTraceComplete: (id) => api.get(`/bijoux/${id}/trace-complete`),
  getCoutDetail: (id) => api.get(`/bijoux/${id}/cout-detail`),
};

export const stocksAPI = {
  getAll: () => api.get('/stocks'),
  getByBoutique: (id) => api.get(`/stocks/boutique/${id}`),
  getByBijouBoutique: (bijouId, boutiqueId) => api.get(`/stocks/bijou/${bijouId}/boutique/${boutiqueId}`),
  getByBijou: (id) => api.get(`/stocks/bijou/${id}`),
  ajuster: (data) => api.post('/stocks/ajustement', data),
};

export const clientsAPI = {
  getAll: () => api.get('/clients'),
  getOne: (id) => api.get(`/clients/${id}`),
  create: (data) => api.post('/clients', data),
  update: (id, data) => api.put(`/clients/${id}`, data),
  delete: (id) => api.delete(`/clients/${id}`),
};

export const ventesAPI = {
  getAll: (params) => api.get('/ventes', { params }),
  getByBoutique: (id) => api.get(`/ventes/boutique/${id}`),
  create: (data) => api.post('/ventes', data),
  update: (id, data) => api.put(`/ventes/${id}`, data), // §201 edition centrale
  delete: (id) => api.delete(`/ventes/${id}`),
};

export const transfertsAPI = {
  getAll: (params) => api.get('/transferts', { params }),
  demandeBoutique: (data) => api.post('/transferts/demande-boutique', data),
  centralCreate: (data) => api.post('/transferts/central', data),
  valider: (id, data) => api.put(`/transferts/${id}/valider`, data || {}),
  recevoir: (id) => api.put(`/transferts/${id}/recevoir`),
  annuler: (id) => api.put(`/transferts/${id}/annuler`),
  getPiecesDisponibles: (boutiqueId, search) =>
    api.get('/transferts/pieces-disponibles', { params: { boutique_id: boutiqueId, search } }),
  transfererPieces: (data) => api.post('/transferts/pieces/transferer', data),
};

export const promotionsAPI = {
  getAll: () => api.get('/promotions'),
  getActives: () => api.get('/promotions/actives'),
  getByBijou: (bijouId) => api.get(`/promotions/bijou/${bijouId}`),
  create: (data) => api.post('/promotions', data),
  update: (id, data) => api.put(`/promotions/${id}`, data),
  delete: (id) => api.delete(`/promotions/${id}`),
  calculer: (data) => api.post('/promotions/calculer', data),
};

export const fournisseursAPI = {
  getAll: (actifOnly = false) => api.get(`/fournisseurs${actifOnly ? '?actif=true' : ''}`),
  getOne: (id) => api.get(`/fournisseurs/${id}`),
  create: (data) => api.post('/fournisseurs', data),
  update: (id, data) => api.put(`/fournisseurs/${id}`, data),
  delete: (id) => api.delete(`/fournisseurs/${id}`),
  getStatistiques: (id) => api.get(`/fournisseurs/${id}/statistiques`),
};

export const achatsAPI = {
  getAll: (params) => api.get('/achats', { params }),
  getOne: (id) => api.get(`/achats/${id}`),
  create: (data) => api.post('/achats', data),
  update: (id, data) => api.put(`/achats/${id}`, data),
  valider: (id) => api.put(`/achats/${id}/valider`),
  recevoir: (id, data) => api.put(`/achats/${id}/recevoir`, data),
  ajouterPaiement: (id, data) => api.post(`/achats/${id}/paiement`, data),
  getStatistiques: (periode) => api.get(`/achats/statistiques?periode=${periode}`),
};

export const atelierAPI = {
  // Ordres de fabrication
  getFabrication: (params) => api.get('/atelier/fabrication', { params }),
  getOneFabrication: (id) => api.get(`/atelier/fabrication/${id}`),
  createFabrication: (data) => api.post('/atelier/fabrication', data),
  updateFabrication: (id, data) => api.put(`/atelier/fabrication/${id}`, data),
  
  // Réparations
  getReparations: (params) => api.get('/atelier/reparations', { params }),
  getOneReparation: (id) => api.get(`/atelier/reparations/${id}`),
  createReparation: (data) => api.post('/atelier/reparations', data),
  updateReparation: (id, data) => api.put(`/atelier/reparations/${id}`, data),
  
  // Composants
  getComposants: (params) => api.get('/atelier/composants', { params }),
  createComposant: (data) => api.post('/atelier/composants', data),
  
  // Statistiques
  getStatistiques: () => api.get('/atelier/statistiques'),
};

export const dashboardAPI = {
  global: () => api.get('/dashboard/global'),
  boutique: (id) => api.get(`/dashboard/boutique/${id}`),
  chartsCaMensuel: () => api.get('/dashboard/charts/ca-mensuel'),
  chartsCategories: () => api.get('/dashboard/charts/categories'),
  chartsBoutiques: () => api.get('/dashboard/charts/boutiques'),
};

export const notificationsAPI = {
  getAll: () => api.get('/notifications'),
};

export default api;
